/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DataShareCreateBasic } from '../../types/dataShare';
import G17Client from '../../services/G17Client';
import { BasicAlert } from '../alert/BasicAlert';
import { DataShareForm } from './DataShareForm';
import { useSiteAlert } from '../../hooks/useSiteAlert';

interface Props extends DataShareCreateBasic {
  initiativeName: string;
  isOpen: boolean;
  toggle: (update?: { reload?: boolean }) => void;
}

interface MessageTextProps {
  requesterName: string;
}

export const getDefaultContent = ({ requesterName }: MessageTextProps) => {
  return `G17Eco Portfolio Tracker user ${requesterName} has requested access to your company’s sustainability reporting data.`;
};

export const DataShareModal = (props: Props) => {
  const {
    requesterId,
    initiativeId,
    initiativeName,
    requesterType,
    title,
    content,
    dataScope,
    isOpen,
    toggle,
  } = props;

  const { addSiteAlert } = useSiteAlert();
  const [isSaving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [form, setForm] = useState<DataShareCreateBasic>({
    requesterId,
    requesterType,
    initiativeId,
    title,
    content,
    dataScope,
  });

  const hasTitle = form.title.length > 0;

  const onSubmit = () => {
    setSaving(true);
    G17Client.createDataShareRequest(form)
      .then(() => {
        addSiteAlert({
          content: `Your share request to ${initiativeName} has been sent.`,
          timeout: 5000,
        });
        setSaving(false);
        toggle({ reload: true });
      })
      .catch((e) => {
        setErrorMessage(e.message);
        setSaving(false);
      });
  };

  const onChange = (u: Partial<DataShareCreateBasic>) => setForm((f) => ({ ...f, ...u }));

  return (
    <Modal isOpen={isOpen} toggle={() => toggle()} backdrop='static'>
      <ModalHeader toggle={() => toggle()}>Data share request</ModalHeader>
      <ModalBody className='pb-0'>
        <BasicAlert type={'danger'}>{errorMessage}</BasicAlert>
        <DataShareForm form={form} onChange={onChange} disabled={isSaving} />
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' disabled={isSaving} onClick={() => toggle()}>
          <span>Cancel</span>
        </Button>
        <Button color='primary' disabled={isSaving || !hasTitle} onClick={onSubmit}>
          Send
        </Button>
      </ModalFooter>
    </Modal>
  );
};
